import type { RowSelectionState } from "@tanstack/react-table";
import { pluralizeWithCount } from "@common/helpers/string";
import type { ReportSettingsTemplateApply } from "@common/schemas/domains/reportSettings/report-settings-template-application.schema";

import { parseRowId } from "./applyReportTemplateRows";

type TargetIds = ReportSettingsTemplateApply["apply"];

const getCheckedIds = (selection: RowSelectionState) =>
    Object.keys(selection).filter((id) => selection[id]);

const toTargetIds = (rowIds: string[]): TargetIds => {
    const targets = rowIds.map(parseRowId);
    return {
        workspaceIds: targets.flatMap((target) =>
            target.kind === "workspace" ? [target.workspaceId] : [],
        ),
        entityIds: targets.flatMap((target) =>
            target.kind === "entity" ? [target.entityId] : [],
        ),
    };
};

export const getApplyReportTemplateChanges = ({
    savedSelection,
    selection,
}: {
    savedSelection: RowSelectionState;
    selection: RowSelectionState;
}): Pick<ReportSettingsTemplateApply, "apply" | "remove"> => ({
    apply: toTargetIds(
        getCheckedIds(selection).filter((id) => !savedSelection[id]),
    ),
    remove: toTargetIds(
        getCheckedIds(savedSelection).filter((id) => !selection[id]),
    ),
});

const countTargets = ({ workspaceIds, entityIds }: TargetIds) =>
    workspaceIds.length + entityIds.length;

export const hasApplyReportTemplateChanges = ({
    apply,
    remove,
}: Pick<ReportSettingsTemplateApply, "apply" | "remove">) =>
    countTargets(apply) + countTargets(remove) > 0;

const describeTargets = ({ workspaceIds, entityIds }: TargetIds) =>
    [
        { word: "client", count: workspaceIds.length },
        { word: "entity", count: entityIds.length },
    ]
        .filter(({ count }) => count > 0)
        .map(({ word, count }) => pluralizeWithCount(word, count))
        .join(" and ");

export const getApplyReportTemplateSuccessText = ({
    templateName,
    apply,
    remove,
}: {
    templateName: string;
} & Pick<ReportSettingsTemplateApply, "apply" | "remove">) => {
    const appliedText = `Applied "${templateName}" to ${describeTargets(apply)}`;
    if (countTargets(remove) === 0) {
        return appliedText;
    }
    if (countTargets(apply) === 0) {
        return `Removed "${templateName}" from ${describeTargets(remove)}`;
    }
    return `${appliedText} and removed it from ${describeTargets(remove)}`;
};
